import React, {useEffect, useState} from "react";
import {
  Box,
  IconButton,
  InputBase,
  Popover,
  Stack,
  SxProps,
} from "@mui/material";
import {Theme} from "@emotion/react";
import {useThemeContext} from "@theme";
import {sort} from "@hooks/useSearchParams";
import {DraggableField} from "./DraggableField";
import {CancelIcon, SearchIcon, SortIcon} from "@components/icons";
import {entity} from "@core/model";

type Props = {
  containerStyle?: SxProps<Theme>;
  open?: boolean;
  entity: entity;
  sort?: {
    value: sort;
    onSort: (sort: string) => void;
  };
  onSearch: (text: string) => void;
  onClear: () => void;
  onFocus?: () => void;
  onBlur?: () => void;
};

function InputSearch(props: Props) {
  const {theme} = useThemeContext();

  const {
    containerStyle,
    sort: sortProp,
    open,
    entity,
    onClear,
    onSearch,
    onFocus,
    onBlur,
  } = props;

  const [toggle, setToggle] = useState<boolean>(open ?? false);
  const [text, setText] = useState<string>();
  const [sort, setSort] = useState<sort>(sortProp?.value ?? {});
  const [fields, setFields] = useState<string[]>(
    Object.keys(sortProp?.value ?? {}),
  );

  const handleChange = (text: string) => {
    setText(text);
  };

  const handleToggle = () => {
    setToggle(!toggle);
  };

  useEffect(() => {
    if (toggle && onFocus) {
      onFocus();
    } else if (!toggle && onBlur) {
      onBlur();
    }
  }, [toggle]);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (text === undefined) return;
      if (text.length > 1) {
        onSearch(text);
      } else {
        onClear();
      }
    }, 500);

    // Cancella il timeout precedente
    return () => {
      clearTimeout(handler);
    };
  }, [text]);

  const Clear = (
    <IconButton
      sx={{fontSize: "0.8rem", p: 0.5, mr: 0.5}}
      onClick={() => {
        setText("");
        setToggle(false);
      }}>
      <CancelIcon fontSize="inherit" />
    </IconButton>
  );
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null,
  );

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleToggleSort = (field: string) => {
    const newSort = sort ?? {};

    if (newSort[field] === "DESC") {
      newSort[field] = "ASC";
    } else if (newSort[field] === "ASC") {
      newSort[field] = "DESC";
    }

    setSort(newSort);

    const sortedEntries = fields
      .filter(f => newSort[f]) // Mantiene solo i campi ordinati
      .map(f => [f, newSort[f]]) // Ricrea l'array rispettando l'ordine di fields
      .flat()
      .join();

    sortProp?.onSort(sortedEntries);
  };

  const moveField = (fromIndex: number, toIndex: number) => {
    const updatedFields = [...fields];
    const [movedField] = updatedFields.splice(fromIndex, 1);
    updatedFields.splice(toIndex, 0, movedField);

    const sortedEntries = updatedFields
      .filter(f => sort[f]) // Mantiene solo i campi ordinati
      .map(f => [f, sort[f]]) // Ricrea l'array rispettando l'ordine di fields
      .flat()
      .join();
    sortProp?.onSort(sortedEntries);

    sortProp?.onSort(sortedEntries);
    setFields(updatedFields);
  };

  const sortOpen = Boolean(anchorEl);
  const id = sortOpen ? "simple-popover" : undefined;

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={1}
      sx={{
        ...containerStyle,
      }}>
      {toggle ? (
        <Box
          className="search-container"
          pl={2}
          pr={1}
          borderRadius={16}
          sx={{
            bgcolor: theme.colors.grey,
          }}>
          <InputBase
            startAdornment={<SearchIcon fontSize="small" sx={{pr: 1}} />}
            endAdornment={Clear}
            autoFocus
            onChange={e => handleChange(e.target.value)}
            value={text ?? ""}
            sx={{display: "flex", height: 40}}
          />
        </Box>
      ) : (
        <IconButton onClick={handleToggle} size="medium">
          <SearchIcon fontSize="inherit" />
        </IconButton>
      )}
      {sortProp && (
        <Box className="sort-container">
          <IconButton onClick={handleClick} size="medium">
            <SortIcon fontSize="inherit" />
          </IconButton>
          <Popover
            id={id}
            open={sortOpen}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}>
            <Stack>
              {fields.map((f, index) => (
                <DraggableField
                  key={"sort_" + f}
                  entity={entity}
                  field={f}
                  index={index}
                  moveField={moveField}
                  sort={sort[f]}
                  onSort={handleToggleSort}
                />
              ))}
            </Stack>
          </Popover>
        </Box>
      )}
    </Stack>
  );
}

export {InputSearch};
